import React, { JSX, useEffect, useMemo } from "react";
import { RouteUtils } from "utilities/route-utils";
import { sitemap } from "sitemap";
import { Icons } from "components/icons/constants/icons";
import { NumberUtils } from "utilities/number-utils";
import { useNavigate } from "utilities/hooks/navigation/use-navigate";
import { Outlet, Location, useLocation, useParams } from "react-router";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { EnrollmentContext } from "utilities/contexts/use-enrollment-context";
import { useEnrollment } from "utilities/hooks/models/enrollments/use-enrollment";
import {
    LearnerTrainingLayoutSidebar,
    SidebarNavItem,
} from "./learner-training-layout-sidebar/learner-training-layout-sidebar";
import { InstructorLedTrainingTypeDisplayNames } from "models/enumerations/products/instructor-led-training-type";
import { Language } from "models/enumerations/languages/language";
import { PublishStatus } from "models/enumerations/publish-status/publish-status";
import { ProductCourseTypeDisplayNames } from "models/enumerations/courses/product-course-type";
import { TrainingType } from "models/enumerations/courses/training-type";
import { t } from "utilities/localization/t";
import { AudienceType } from "models/enumerations/audiences/audience-type";
import "./learner-training-layout.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface LearnerTrainingLayoutProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "learner-training-layout";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const LearnerTrainingLayout: React.FC<LearnerTrainingLayoutProps> = (): JSX.Element => {
    const location: Location = useLocation();
    const navigate = useNavigate();

    const {
        id: eventIdParam,
        enrollmentId: enrollmentIdParam,
        productId: productIdParam,
    } = useParams();

    const enrollmentId = useMemo(
        () => NumberUtils.parseInt(enrollmentIdParam) ?? 0,
        [enrollmentIdParam]
    );

    const productId = useMemo(() => NumberUtils.parseInt(productIdParam) ?? 0, [productIdParam]);
    const eventId = useMemo(() => NumberUtils.parseInt(eventIdParam) ?? 0, [eventIdParam]);

    const { enrollment, setEnrollment, isLoading } = useEnrollment({
        enrollmentId,
        includeEvent: true,
        includeProduct: true,
        includeEvaluation: true,
        includeTrainingType: TrainingType.OnlineLearning,
    });

    const badges: string[] = [];

    // ILT Training Badge
    if (enrollment?.event?.instructorLedTrainingType != null) {
        badges.push(
            t(InstructorLedTrainingTypeDisplayNames[enrollment.event.instructorLedTrainingType])
        );
    }

    // Online / TTT / AEN Resource Badge (Course/Series | Learning Path)
    if (enrollment?.product?.onlineLearningType != null) {
        badges.push(t(ProductCourseTypeDisplayNames[enrollment?.product.onlineLearningType]));
    }

    if (enrollment?.product?.language != null) {
        badges.push(Language[enrollment.product?.language]);
    }

    if (
        enrollment?.productVersion != null &&
        enrollment?.product?.audienceType === AudienceType.Learner
    ) {
        const ceus = enrollment.event?.getCeus() ?? enrollment.productVersion?.getCeusDisplay();

        badges.push(
            t("ceusEnrollmentProductCreditHoursToString", {
                enrollmentProductCreditHoursToString: ceus,
            })
        );
    }

    const navItems: SidebarNavItem[] = useMemo(() => {
        if (enrollmentId == null) {
            return [];
        }

        if (eventId != null && eventId > 0) {
            return [
                {
                    path: RouteUtils.replacePathParams(sitemap.learner.training.event.details, {
                        id: eventId,
                        enrollmentId: enrollmentId,
                    }),
                    displayName: t("about"),
                    iconType: Icons.EventDetails,
                },
                {
                    path: RouteUtils.replacePathParams(sitemap.learner.training.event.content, {
                        id: eventId,
                        enrollmentId: enrollmentId,
                    }),
                    disabled: enrollment?.event?.status === PublishStatus.Canceled,
                    displayName: t("content"),
                    iconType: Icons.Content,
                },
                {
                    matchPathOnStartsWith: true,
                    path: RouteUtils.replacePathParams(
                        sitemap.learner.training.event.assessments.list,
                        {
                            id: eventId,
                            enrollmentId: enrollmentId,
                        }
                    ),
                    disabled: enrollment?.event?.status === PublishStatus.Canceled,
                    displayName: t("assessment"),
                    iconType: Icons.Assessment,
                },
                ...(enrollment?.product?.hasNoEvaluation === false ? [{
                    path: RouteUtils.replacePathParams(
                        sitemap.learner.training.onlineLearning.evaluation,
                        {
                            productId: productId,
                            enrollmentId: enrollmentId,
                        }
                    ),
                    displayName: t("evaluation"),
                    iconType: Icons.Evaluation,
                }] : []),
                {
                    path: RouteUtils.replacePathParams(
                        sitemap.learner.training.event.achievements,
                        {
                            id: eventId,
                            enrollmentId: enrollmentId,
                        }
                    ),
                    disabled: enrollment?.event?.status === PublishStatus.Canceled,
                    displayName: t("achievements"),
                    iconType: Icons.Achievements,
                },
            ];
        }

        if (enrollment?.product?.type === TrainingType.OnlineLearning) {
            return [
                {
                    matchPathOnStartsWith: true,
                    path: RouteUtils.replacePathParams(
                        sitemap.learner.training.onlineLearning.details,
                        {
                            productId: productId,
                            enrollmentId: enrollmentId,
                        }
                    ),
                    displayName: t("about"),
                    iconType: Icons.EventDetails,
                },
                {
                    path: RouteUtils.replacePathParams(
                        sitemap.learner.training.onlineLearning.content,
                        {
                            productId: productId,
                            enrollmentId: enrollmentId,
                        }
                    ),
                    displayName: t("content"),
                    iconType: Icons.Content,
                },
                ...(enrollment?.product?.hasNoEvaluation === false ? [{
                    path: RouteUtils.replacePathParams(
                        sitemap.learner.training.onlineLearning.evaluation,
                        {
                            productId: productId,
                            enrollmentId: enrollmentId,
                        }
                    ),
                    displayName: t("evaluation"),
                    iconType: Icons.Evaluation,
                }] : []),
                {
                    path: RouteUtils.replacePathParams(
                        sitemap.learner.training.onlineLearning.achievements,
                        {
                            productId: productId,
                            enrollmentId: enrollmentId,
                        }
                    ),
                    displayName: t("achievements"),
                    iconType: Icons.Achievements,
                },
            ];
        }

        if (enrollment?.product?.audienceType === AudienceType.EducationNetwork) {
            return [
                {
                    matchPathOnStartsWith: true,
                    path: RouteUtils.replacePathParams(
                        sitemap.learner.training.onlineLearning.details,
                        {
                            productId: productId,
                            enrollmentId: enrollmentId,
                        }
                    ),
                    displayName: t("about"),
                    iconType: Icons.EventDetails,
                },
                {
                    path: RouteUtils.replacePathParams(
                        sitemap.learner.training.onlineLearning.content,
                        {
                            productId: productId,
                            enrollmentId: enrollmentId,
                        }
                    ),
                    displayName: t("content"),
                    iconType: Icons.Content,
                },
            ];
        }

        // return empty string if we get here.
        return [];
    }, [
        enrollment?.event?.status,
        enrollment?.product?.audienceType,
        enrollment?.product?.type,
        enrollmentId,
        eventId,
        productId,
    ]);

    useEffect(() => {
        const aboutPath = RouteUtils.replacePathParams(sitemap.learner.training.event.details, {
            id: eventId,
            enrollmentId: enrollmentId,
        });

        if (enrollment?.withdrawnOn != null) {
            navigate(sitemap.public.noAccess);
        }

        if (
            enrollment?.event?.status === PublishStatus.Canceled &&
            location.pathname !== aboutPath
        ) {
            navigate(aboutPath);
        }
    }, [
        enrollment?.event?.status,
        enrollment?.withdrawnOn,
        enrollmentId,
        eventId,
        location.pathname,
        navigate,
    ]);

    return (
        <EnrollmentContext.Provider value={{ record: enrollment, setRecord: setEnrollment }}>
            <div className={CSS_CLASS_NAME}>
                {!isLoading && (
                    <div className={`${CSS_CLASS_NAME}__window`}>
                        <div className={`${CSS_CLASS_NAME}__window__sidebar`}>
                            <LearnerTrainingLayoutSidebar
                                badges={badges}
                                completionPercentage={enrollment?.percentComplete}
                                expirationDate={enrollment?.expirationDate}
                                trainingName={enrollment?.product?.name}
                                trainingType={enrollment!.product?.type}
                                navItems={navItems}
                            />
                        </div>
                        <div className={`${CSS_CLASS_NAME}__window__main`}>
                            <SkipNavContent>
                                <div className={`${CSS_CLASS_NAME}__body`}>
                                    <Outlet />
                                </div>
                            </SkipNavContent>
                        </div>
                    </div>
                )}
            </div>
        </EnrollmentContext.Provider>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { LearnerTrainingLayout };

// #endregion Exports
